/**
 * pages/Admin.tsx - Admin Dashboard
 * Tabs: Users / Pending Reviews / Listings / Categories / Verifications
 * Only accessible to ADMIN role
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '@/lib/api-client';
import { verificationApi } from '@/services/verificationApi';
import type { VerificationRecord } from '@/types/verification';
import {
  Shield,
  Users,
  LayoutGrid,
  Tag,
  Loader2,
  AlertCircle,
  CheckCircle2,
  XCircle,
  ChevronLeft,
  ChevronRight,
  Eye,
  BadgeCheck,
  FileImage,
  MessageSquare,
  X,
} from 'lucide-react';
import { formatCategory } from '@/lib/utils';
import SEO from '@/components/common/SEO';

type AdminTab = 'users' | 'pending' | 'listings' | 'categories' | 'verifications';

// Types
interface AdminUser {
  id: string;
  email: string;
  name: string;
  role: string;
  isLocked: boolean;
  isVerified?: boolean;
  createdAt: any;
}

interface AdminListing {
  id: string;
  title: string;
  category: string;
  price: number;
  status: string;
  sellerId: string;
  createdAt: any;
}

interface AdminCategory {
  id: string;
  name: string;
  slug: string;
  totalListings: number;
  status: string;
}

// Helper to format java LocalDateTime (array) or Instant (ISO string) safely
const formatUserDate = (dateVal: any): string => {
  if (!dateVal) return 'N/A';
  
  if (Array.isArray(dateVal)) {
    // LocalDateTime array: [year, month, day, hour, minute, second, nano]
    const [year, month, day, hour = 0, minute = 0, second = 0] = dateVal;
    // JS Date month is 0-indexed, Spring month is 1-indexed (1-12)
    const dateObj = new Date(year, month - 1, day, hour, minute, second);
    return dateObj.toLocaleDateString('en-IN');
  }

  const d = new Date(dateVal);
  if (isNaN(d.getTime())) {
    return 'N/A';
  }
  return d.toLocaleDateString('en-IN');
};

const AdminPage: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<AdminTab>('users');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Data
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [listings, setListings] = useState<AdminListing[]>([]);
  const [categories, setCategories] = useState<AdminCategory[]>([]);
  const [pendingListings, setPendingListings] = useState<AdminListing[]>([]);
  const [pendingCount, setPendingCount] = useState<number>(0);

  // Pagination
  const [userPage, setUserPage] = useState(0);
  const [listingPage, setListingPage] = useState(0);
  const [pendingPage, setPendingPage] = useState(0);
  const [verificationPage, setVerificationPage] = useState(0);

  const PAGE_SIZE = 20;

  // Verification state
  const [verifications, setVerifications] = useState<VerificationRecord[]>([]);
  const [verificationCount, setVerificationCount] = useState(0);
  const [verificationTotal, setVerificationTotal] = useState(0);
  const [reviewModalId, setReviewModalId] = useState<string | null>(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [documentPreview, setDocumentPreview] = useState<{ id: string; side: 'front' | 'back' } | null>(null);
  const [documentPreviewUrl, setDocumentPreviewUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [userMap, setUserMap] = useState<Record<string, { name: string; email: string }>>({});

  // Auth guard
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
    } else if (user?.role !== 'ADMIN') {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, user, navigate]);

  // Handle document preview — fetch image bytes through backend proxy (avoids MinIO internal hostname)
  useEffect(() => {
    if (documentPreview) {
      setPreviewLoading(true);
      const token = localStorage.getItem('accessToken');
      const baseUrl = import.meta.env.VITE_API_BASE_URL || '';
      fetch(`${baseUrl}/api/admin/verification/document/${documentPreview.id}/${documentPreview.side}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
        .then(res => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          return res.blob();
        })
        .then(blob => {
          const url = URL.createObjectURL(blob);
          setDocumentPreviewUrl(url);
        })
        .catch(err => {
          console.error("Failed to fetch document image", err);
          setDocumentPreviewUrl(null);
        })
        .finally(() => setPreviewLoading(false));
    } else {
      setDocumentPreviewUrl(prev => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
    }
  }, [documentPreview]);

  // Fetch pending count
  const fetchPendingCount = async () => {
    try {
      const res = await apiClient.get('/api/listings/pending/count');
      setPendingCount(res?.count || 0);
    } catch (err) {
      console.error('Failed to fetch pending count:', err);
    }
  };

  // Fetch verification counts
  const fetchVerificationCounts = async () => {
    try {
      const res = await verificationApi.getPendingCount();
      setVerificationCount(res?.count || 0);
    } catch (err) {
      console.error('Failed to fetch verification count:', err);
    }
  };

  // Load data when tab or pagination changes
  useEffect(() => {
    loadData();
    fetchPendingCount();
    fetchVerificationCounts();
  }, [activeTab, userPage, listingPage, pendingPage, verificationPage]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      if (activeTab === 'users') {
        // User-service: GET /api/users/admin/candidates → Spring Page<User> format
        const data = await apiClient.get(`/api/users/admin/candidates?page=${userPage}&size=${PAGE_SIZE}`);
        // Spring Page response has { content: [...], totalElements, ... }
        const content = data?.content || [];
        setUsers(content.map((u: any) => ({
          id: String(u.id),
          email: u.email || '',
          name: u.name || '',
          role: u.role || 'CUSTOMER',
          isLocked: u.isLocked || u.locked || false,
          isVerified: Boolean(u.isVerified || u.verified),
          createdAt: u.createdAt || '',
        })));
      } else if (activeTab === 'pending') {
        // Listing-service: GET /api/listings/pending → Spring Page<ListingResponse> format
        const data = await apiClient.get(`/api/listings/pending?page=${pendingPage}&size=${PAGE_SIZE}`);
        const content = data?.content || [];
        setPendingListings(content.map((l: any) => ({
          id: String(l.id),
          title: l.title || '',
          category: l.category || '',
          price: l.price || 0,
          status: l.status || 'PENDING_REVIEW',
          sellerId: l.sellerId || '',
          createdAt: l.createdAt || '',
        })));
      } else if (activeTab === 'listings') {
        // Listing-service: GET /api/listings → Spring Page<ListingResponse> format
        const data = await apiClient.get(`/api/listings?page=${listingPage}&size=${PAGE_SIZE}`);
        const content = data?.content || [];
        setListings(content.map((l: any) => ({
          id: String(l.id),
          title: l.title || '',
          category: l.category || '',
          price: l.price || 0,
          status: l.status || 'ACTIVE',
          sellerId: l.sellerId || '',
          createdAt: l.createdAt || '',
        })));
      } else if (activeTab === 'categories') {
        // Listing-service: GET /api/listings/category-counts → { categoryName: count, ... }
        const data = await apiClient.get(`/api/listings/category-counts`);
        // Transform map to array format
        const catArray: AdminCategory[] = Object.entries(data || {}).map(([name, count]) => ({
          id: name,
          name,
          slug: name.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
          totalListings: count as number,
          status: 'ACTIVE',
        }));
        setCategories(catArray);
      } else if (activeTab === 'verifications') {
        const data = await verificationApi.getQueue({
          page: verificationPage,
          size: PAGE_SIZE,
        });
        setVerifications(data?.content || []);
        setVerificationTotal(data?.totalElements || 0);

        // Fetch users to build keycloakId → {name, email} map for display
        try {
          const userData = await apiClient.get(`/api/users/admin/candidates?page=0&size=200`);
          const map: Record<string, { name: string; email: string }> = {};
          (userData?.content || []).forEach((u: any) => {
            if (u.keycloakId) {
              map[u.keycloakId] = { name: u.name || '', email: u.email || '' };
            }
          });
          setUserMap(map);
        } catch (e) {
          console.warn('Could not fetch user details for verifications:', e);
        }
      }
    } catch (err: any) {
      console.error('Error loading admin data:', err);
      setError(err?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Verification review handler
  const handleVerificationReview = async (id: string, status: 'APPROVED' | 'REJECTED') => {
    setActionLoading(id);
    try {
      await verificationApi.reviewVerification(id, { status, adminNotes: reviewNotes });
      setReviewModalId(null);
      setReviewNotes('');
      // Refresh
      const data = await verificationApi.getQueue({
        page: verificationPage,
        size: PAGE_SIZE,
      });
      setVerifications(data?.content || []);
      setVerificationTotal(data?.totalElements || 0);
      setVerificationCount(c => Math.max(0, c - 1));
    } catch (err: any) {
      setError(err?.message || `Failed to ${status.toLowerCase()} verification`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleUserAction = async (userId: string, action: 'ban' | 'activate') => {
    setActionLoading(userId);
    try {
      // User-service: PUT /api/users/admin/candidates/{id}/lock or /unlock
      const endpoint = action === 'ban' ? 'lock' : 'unlock';
      await apiClient.put(`/api/users/admin/candidates/${userId}/${endpoint}`);
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, isLocked: action === 'ban' } : u));
    } catch (err: any) {
      setError(err?.message || `Failed to ${action} user`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleListingAction = async (listingId: string, action: 'remove' | 'approve' | 'reject') => {
    setActionLoading(listingId);
    try {
      if (action === 'remove') {
        await apiClient.delete(`/api/listings/${listingId}`);
        setListings(prev => prev.filter(l => l.id !== listingId));
      } else if (action === 'approve') {
        await apiClient.put(`/api/listings/${listingId}/approve`);
        setPendingListings(prev => prev.filter(l => l.id !== listingId));
        setPendingCount(c => Math.max(0, c - 1));
      } else if (action === 'reject') {
        await apiClient.put(`/api/listings/${listingId}/reject`);
        setPendingListings(prev => prev.filter(l => l.id !== listingId));
        setPendingCount(c => Math.max(0, c - 1));
      }
    } catch (err: any) {
      setError(err?.message || `Failed to ${action} listing`);
    } finally {
      setActionLoading(null);
    }
  };

  const TABS = [
    { id: 'users' as AdminTab, label: 'Users', icon: Users },
    { id: 'pending' as AdminTab, label: 'Pending Reviews', icon: Shield, badge: pendingCount },
    { id: 'listings' as AdminTab, label: 'Listings', icon: LayoutGrid },
    { id: 'categories' as AdminTab, label: 'Categories', icon: Tag },
    { id: 'verifications' as AdminTab, label: 'Verifications', icon: BadgeCheck, badge: verificationCount },
  ];

  return (
    <div className="min-h-screen bg-background py-8 px-4 sm:px-6 lg:px-8">
      <SEO title="Admin Dashboard" noindex />
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground flex items-center gap-2">
            <Shield className="h-8 w-8 text-destructive" />
            Admin Dashboard
          </h1>
          <p className="text-muted-foreground mt-1">Manage users, listings, and categories</p>
        </div>

        {/* Error Banner */}
        {error && (
          <div className="mb-6 rounded-xl border border-destructive/30 bg-destructive/5 p-4 flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-destructive shrink-0" />
            <p className="text-sm text-destructive flex-1">{error}</p>
            <button onClick={() => setError(null)} className="text-xs text-destructive hover:underline">Dismiss</button>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-border">
          {TABS.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  if (tab.id === 'users') setUserPage(0);
                  if (tab.id === 'pending') setPendingPage(0);
                  if (tab.id === 'listings') setListingPage(0);
                  if (tab.id === 'verifications') setVerificationPage(0);
                }}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition ${
                  activeTab === tab.id
                    ? 'border-destructive text-foreground'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon className="h-4 w-4" />
                <span>{tab.label}</span>
                {tab.badge !== undefined && tab.badge > 0 && (
                  <span className="inline-flex items-center justify-center px-2 py-0.5 text-xs font-semibold rounded-full bg-amber-500 text-white leading-none">
                    {tab.badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        )}

        {/* Users Tab */}
        {!loading && activeTab === 'users' && (
          <div className="rounded-xl border border-border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">User</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Email</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Role</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Joined</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {users.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center py-12 text-muted-foreground">No users found</td>
                    </tr>
                  ) : users.map(u => (
                    <tr key={u.id} className="hover:bg-muted/30 transition">
                      <td className="px-4 py-3">
                        <div className="font-medium text-foreground">{u.name || 'Unnamed'}</div>
                        <div className="text-xs text-muted-foreground">ID: {u.id.slice(0, 8)}...</div>
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">{u.email}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                          u.role === 'ADMIN' ? 'bg-red-50 text-red-700' : 'bg-blue-50 text-blue-700'
                        }`}>
                          {u.role}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col items-start gap-1">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                            u.isLocked ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'
                          }`}>
                            {u.isLocked ? <XCircle className="h-3 w-3" /> : <CheckCircle2 className="h-3 w-3" />}
                            {u.isLocked ? 'Locked' : 'Active'}
                          </span>
                          {u.isVerified ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                              <BadgeCheck className="h-3 w-3 text-emerald-600" />
                              Aadhaar Verified
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-muted text-muted-foreground border border-border">
                              Not Verified
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">
                        {formatUserDate(u.createdAt)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {u.role !== 'ADMIN' && (
                          <button
                            onClick={() => handleUserAction(u.id, u.isLocked ? 'activate' : 'ban')}
                            disabled={actionLoading === u.id}
                            className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition ${
                              u.isLocked
                                ? 'border border-green-300 text-green-700 hover:bg-green-50'
                                : 'border border-red-300 text-red-700 hover:bg-red-50'
                            }`}
                          >
                            {actionLoading === u.id ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : u.isLocked ? (
                              <CheckCircle2 className="h-3.5 w-3.5" />
                            ) : (
                              <XCircle className="h-3.5 w-3.5" />
                            )}
                            {u.isLocked ? 'Activate' : 'Ban'}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Pagination */}
            {users.length === PAGE_SIZE && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-border">
                <button
                  onClick={() => { setUserPage(p => Math.max(0, p - 1)); loadData(); }}
                  disabled={userPage === 0}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-border text-xs font-medium hover:bg-secondary disabled:opacity-50"
                >
                  <ChevronLeft className="h-3.5 w-3.5" /> Previous
                </button>
                <span className="text-xs text-muted-foreground">Page {userPage + 1}</span>
                <button
                  onClick={() => { setUserPage(p => p + 1); loadData(); }}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-border text-xs font-medium hover:bg-secondary"
                >
                  Next <ChevronRight className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
          </div>
        )}

        {/* Pending Reviews Tab */}
        {!loading && activeTab === 'pending' && (
          <div className="rounded-xl border border-border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Listing</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Category</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Price</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Date</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {pendingListings.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center py-12 text-muted-foreground">No pending listings found</td>
                    </tr>
                  ) : pendingListings.map(l => (
                    <tr key={l.id} className="hover:bg-muted/30 transition">
                      <td className="px-4 py-3">
                        <div className="font-medium text-foreground">{l.title}</div>
                        <div className="text-xs text-muted-foreground">ID: {l.id.slice(0, 8)}...</div>
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">{formatCategory(l.category)}</td>
                      <td className="px-4 py-3 text-sm font-medium text-foreground">₹{l.price.toLocaleString('en-IN')}</td>
                      <td className="px-4 py-3">
                        <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 border border-amber-200">
                          {l.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">
                        {formatUserDate(l.createdAt)}
                      </td>
                      <td className="px-4 py-3 text-right flex justify-end gap-2">
                        <button
                          onClick={() => navigate(`/listings/${l.id}`)}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-border text-foreground text-xs font-medium hover:bg-secondary transition"
                        >
                          <Eye className="h-3.5 w-3.5" />
                          View
                        </button>
                        <button
                          onClick={() => handleListingAction(l.id, 'approve')}
                          disabled={actionLoading === l.id}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-green-600 text-white text-xs font-medium hover:bg-green-700 transition disabled:opacity-50"
                        >
                          {actionLoading === l.id ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : <CheckCircle2 className="h-3.5 w-3.5" />}
                          Approve
                        </button>
                        <button
                          onClick={() => handleListingAction(l.id, 'reject')}
                          disabled={actionLoading === l.id}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-red-300 text-red-700 text-xs font-medium hover:bg-red-50 transition disabled:opacity-50"
                        >
                          {actionLoading === l.id ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : <XCircle className="h-3.5 w-3.5" />}
                          Reject
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Pagination */}
            {pendingListings.length === PAGE_SIZE && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-border">
                <button
                  onClick={() => { setPendingPage(p => Math.max(0, p - 1)); }}
                  disabled={pendingPage === 0}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-border text-xs font-medium hover:bg-secondary disabled:opacity-50"
                >
                  <ChevronLeft className="h-3.5 w-3.5" /> Previous
                </button>
                <span className="text-xs text-muted-foreground">Page {pendingPage + 1}</span>
                <button
                  onClick={() => { setPendingPage(p => p + 1); }}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-border text-xs font-medium hover:bg-secondary"
                >
                  Next <ChevronRight className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
          </div>
        )}

        {/* Listings Tab */}
        {!loading && activeTab === 'listings' && (
          <div className="rounded-xl border border-border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Listing</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Category</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Price</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Date</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {listings.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center py-12 text-muted-foreground">No listings found</td>
                    </tr>
                  ) : listings.map(l => (
                    <tr key={l.id} className="hover:bg-muted/30 transition">
                      <td className="px-4 py-3">
                        <div className="font-medium text-foreground">{l.title}</div>
                        <div className="text-xs text-muted-foreground">ID: {l.id.slice(0, 8)}...</div>
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">{formatCategory(l.category)}</td>
                      <td className="px-4 py-3 text-sm font-medium text-foreground">₹{l.price.toLocaleString('en-IN')}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                          l.status === 'ACTIVE' ? 'bg-green-50 text-green-700' : 'bg-gray-50 text-gray-700'
                        }`}>
                          {l.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">
                        {formatUserDate(l.createdAt)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => navigate(`/listings/${l.id}`)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-border text-foreground text-xs font-medium hover:bg-secondary transition"
                          >
                            <Eye className="h-3.5 w-3.5" />
                            View
                          </button>
                          <button
                            onClick={() => handleListingAction(l.id, 'remove')}
                            disabled={actionLoading === l.id}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-red-300 text-red-700 text-xs font-medium hover:bg-red-50 transition disabled:opacity-50"
                          >
                            {actionLoading === l.id ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : <XCircle className="h-3.5 w-3.5" />}
                            Remove
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Verifications Tab */}
        {!loading && activeTab === 'verifications' && (
          <div className="rounded-xl border border-border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">User</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Email</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Aadhaar</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Documents</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Date</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {verifications.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center py-12 text-muted-foreground">No verifications found</td>
                    </tr>
                  ) : verifications.map(v => (
                    <tr key={v.id} className="hover:bg-muted/30 transition">
                      <td className="px-4 py-3">
                        <div className="font-medium text-foreground">{userMap[v.userId]?.name || 'Unknown'}</div>
                        <div className="text-xs text-muted-foreground">ID: {v.userId.slice(0, 8)}...</div>
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">{userMap[v.userId]?.email || '—'}</td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">
                        {v.aadhaarNumber ? `xxxx xxxx ${v.aadhaarNumber}` : 'Not provided'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          {v.hasFrontImage && (
                            <button
                              onClick={() => setDocumentPreview({ id: v.id, side: 'front' })}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100"
                            >
                              <FileImage className="h-3 w-3" />
                              Front
                            </button>
                          )}
                          {v.hasBackImage && (
                            <button
                              onClick={() => setDocumentPreview({ id: v.id, side: 'back' })}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100"
                            >
                              <FileImage className="h-3 w-3" />
                              Back
                            </button>
                          )}
                          {!v.hasFrontImage && !v.hasBackImage && (
                            <span className="text-xs text-muted-foreground">None</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                          v.status === 'APPROVED' ? 'bg-green-50 text-green-700 border border-green-200' :
                          v.status === 'REJECTED' ? 'bg-red-50 text-red-700 border border-red-200' :
                          'bg-amber-50 text-amber-700 border border-amber-200'
                        }`}>
                          {v.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">
                        {formatUserDate(v.createdAt)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {v.status === 'PENDING' ? (
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => { setReviewModalId(v.id); setReviewNotes(''); }}
                              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-green-600 text-white text-xs font-medium hover:bg-green-700 transition"
                            >
                              <CheckCircle2 className="h-3.5 w-3.5" />
                              Review
                            </button>
                          </div>
                        ) : (
                          <div className="flex justify-end gap-2">
                            {v.adminNotes && (
                              <span className="text-xs text-muted-foreground italic max-w-[120px] truncate" title={v.adminNotes}>
                                Note: {v.adminNotes}
                              </span>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Pagination */}
            {(verifications.length === PAGE_SIZE || verificationPage > 0) && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-border">
                <button
                  onClick={() => setVerificationPage(p => Math.max(0, p - 1))}
                  disabled={verificationPage === 0}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-border text-xs font-medium hover:bg-secondary disabled:opacity-50"
                >
                  <ChevronLeft className="h-3.5 w-3.5" /> Previous
                </button>
                <span className="text-xs text-muted-foreground">
                  Page {verificationPage + 1} {verificationTotal > 0 && `(Total: ${verificationTotal})`}
                </span>
                <button
                  onClick={() => setVerificationPage(p => p + 1)}
                  disabled={verifications.length < PAGE_SIZE}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-border text-xs font-medium hover:bg-secondary disabled:opacity-50"
                >
                  Next <ChevronRight className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
          </div>
        )}

        {/* Categories Tab */}
        {!loading && activeTab === 'categories' && (
          <div className="rounded-xl border border-border overflow-hidden">
            <div className="p-4 border-b border-border flex justify-end">
              <button
                onClick={() => {/* TODO: Add category creation modal */}}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:brightness-110 transition text-sm font-semibold"
              >
                Add Category
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Slug</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Listings</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Status</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {categories.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="text-center py-12 text-muted-foreground">No categories found</td>
                    </tr>
                  ) : categories.map(c => (
                    <tr key={c.id} className="hover:bg-muted/30 transition">
                      <td className="px-4 py-3 font-medium text-foreground">{c.name}</td>
                      <td className="px-4 py-3 text-sm text-muted-foreground font-mono">{c.slug}</td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">{c.totalListings}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                          c.status === 'ACTIVE' ? 'bg-green-50 text-green-700' : 'bg-gray-50 text-gray-700'
                        }`}>
                          {c.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-border text-xs font-medium text-foreground hover:bg-secondary transition">
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Review Modal */}
      {reviewModalId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setReviewModalId(null)} />
          <div className="relative bg-background rounded-2xl shadow-xl border border-border w-full max-w-md mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-foreground flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-primary" />
                Review Verification
              </h3>
              <button onClick={() => setReviewModalId(null)} className="p-1 rounded-lg hover:bg-secondary">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label htmlFor="reviewNotes" className="block text-sm font-medium text-foreground mb-1.5">
                  Admin Notes (optional)
                </label>
                <textarea
                  id="reviewNotes"
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  placeholder="Add notes about this verification..."
                  rows={3}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/20 resize-none"
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => handleVerificationReview(reviewModalId, 'REJECTED')}
                  disabled={actionLoading === reviewModalId}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-red-300 text-red-700 hover:bg-red-50 transition font-medium text-sm disabled:opacity-50"
                >
                  {actionLoading === reviewModalId ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <XCircle className="h-4 w-4" />
                  )}
                  Reject
                </button>
                <button
                  onClick={() => handleVerificationReview(reviewModalId, 'APPROVED')}
                  disabled={actionLoading === reviewModalId}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-green-600 text-white hover:bg-green-700 transition font-medium text-sm disabled:opacity-50"
                >
                  {actionLoading === reviewModalId ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <CheckCircle2 className="h-4 w-4" />
                  )}
                  Approve
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Document Preview Modal */}
      {documentPreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/70" onClick={() => setDocumentPreview(null)} />
          <div className="relative bg-background rounded-2xl border border-border max-w-2xl w-full mx-4 max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h3 className="text-lg font-bold text-foreground capitalize">
                {documentPreview.side} Side of Aadhaar
              </h3>
              <button onClick={() => setDocumentPreview(null)} className="p-1 rounded-lg hover:bg-secondary">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-4 flex-1 overflow-auto flex items-center justify-center bg-black/5 rounded-b-2xl">
              {previewLoading ? (
                <div className="flex flex-col items-center gap-2 text-muted-foreground">
                  <Loader2 className="h-8 w-8 animate-spin" />
                  <span className="text-sm font-medium">Loading image...</span>
                </div>
              ) : documentPreviewUrl ? (
                <img
                  src={documentPreviewUrl}
                  alt={`${documentPreview.side} Aadhaar`}
                  className="max-w-full max-h-[70vh] object-contain rounded-lg shadow-sm"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = '';
                    (e.target as HTMLImageElement).alt = 'Failed to load image';
                  }}
                />
              ) : (
                <div className="text-muted-foreground font-medium flex flex-col items-center gap-2">
                  <AlertCircle className="h-8 w-8 text-destructive/70" />
                  <span>Failed to load image</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPage;